import { create } from "zustand";
import { devtools } from "zustand/middleware";

export type Workspace = {
  id: string;
  name: string;
  /** The portal's own name (set by the owning OEM). Only present for PORTAL workspaces. */
  portalName?: string;
  type?: "OEM" | "PORTAL";
  oem_id: string;
  icon?: string;
  plan?: string;
  members?: number;
  /**
   * Ancestor connection ids of this portal (PORTAL workspaces only). Used to
   * detect when one membership is nested under another so it is rendered once
   * inside the hierarchy rather than also as a separate root.
   */
  parentConnectionIds?: string[];
  /**
   * Whether the user may create sub portals beneath this portal (PORTAL
   * workspaces only). "Add sub portal" is offered as soon as ANY membership
   * allows it, and the form then asks which portal to create under.
   */
  allowSubPortalCreation?: boolean;
};

/** One owning OEM together with all of its portals the user belongs to. */
export type PortalGroup = {
  oemId: string;
  oemName: string;
  icon?: string;
  portals: Workspace[];
};

/**
 * Collapse the flat PORTAL workspaces into one group per owning OEM, preserving
 * the original ordering of first appearance. OEM-type workspaces are ignored.
 */
export const groupPortalsByOem = (workspaces: Workspace[]): PortalGroup[] => {
  const groups = new Map<string, PortalGroup>();
  for (const ws of workspaces) {
    if (ws.type !== "PORTAL") continue;
    const existing = groups.get(ws.oem_id);
    if (existing) {
      existing.portals.push(ws);
    } else {
      groups.set(ws.oem_id, {
        oemId: ws.oem_id,
        oemName: ws.name,
        icon: ws.icon,
        portals: [ws],
      });
    }
  }
  return Array.from(groups.values());
};

/**
 * The OEM that owns a given portal workspace, looked up by the portal's connection id
 * (the `:id` route param). Returns undefined until the store has been hydrated or when
 * the id belongs to no known portal.
 */
export const getOemIdForPortal = (
  workspaces: Workspace[],
  portalId: string | undefined,
): string | undefined =>
  portalId
    ? workspaces.find((ws) => ws.type === "PORTAL" && ws.id === portalId)
        ?.oem_id
    : undefined;

type WorkspacesState = {
  workspaces: Workspace[];
  setWorkspaces: (workspaces: Workspace[]) => void;
};

export const useWorkspacesStore = create<WorkspacesState>()(
  devtools(
    (set) => ({
      workspaces: [],
      setWorkspaces: (workspaces) => set({ workspaces }),
    }),
    { name: "WorkspacesStore" },
  ),
);
